<!-- Modal Backdrop -->
<div
	class="fixed inset-0 z-[99999] flex items-center justify-center p-4 md:p-6 lg:p-8 overflow-y-auto modal-backdrop"
	(click)="closeModal()"
>
	<!-- Modal Content - Dynamic Width based on step -->
	<div
		class="modal-container relative bg-white rounded-3xl shadow-2xl w-full max-h-[90vh] overflow-hidden grid grid-cols-1 transition-all duration-300"
		[class.max-w-7xl]="currentStep() !== 'datetime'"
		[class.max-w-[1400px]]="currentStep() === 'datetime'"
		[class.lg:grid-cols-[300px_1fr]]="currentStep() !== 'datetime'"
		[class.lg:grid-cols-[320px_1fr]]="currentStep() === 'datetime'"
		(click)="$event.stopPropagation()"
	>
		<!-- ============================================ -->
		<!-- LEFT SIDEBAR - Progress & Summary (Desktop) -->
		<!-- ============================================ -->
		<aside class="hidden lg:flex flex-col sidebar-gradient border-r border-gray-100">
			<!-- Logo/Brand Header -->
			<div class="p-6 border-b border-[#5d202d]/10">
				<div class="flex items-center gap-3">
					<div class="w-12 h-12 rounded-xl bg-[#5d202d] flex items-center justify-center">
						<svg
							class="w-6 h-6 text-white"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
							/>
						</svg>
					</div>
					<div>
						<h3 class="font-bold text-[#5d202d]">Book Appointment</h3>
						<p class="text-xs text-gray-500">OG Studio</p>
					</div>
				</div>
			</div>

			<!-- Progress Steps (4 steps) -->
			<div class="flex-1 p-6 overflow-y-auto">
				<div class="space-y-1">
					@for (step of getVisibleSteps(); track step.id; let i = $index) {
						<div class="flex items-start gap-4 relative">
							<!-- Step Line -->
							@if (i < getVisibleSteps().length - 1) {
								<div
									class="absolute left-[18px] top-10 w-0.5 h-[calc(100%+4px)] -translate-x-1/2"
									[class]="
										getStepIndex(currentStep()) > i
											? 'bg-green-500'
											: 'bg-gray-200'
									"
								></div>
							}

							<!-- Step Circle -->
							<div
								class="relative z-10 flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300"
								[class]="
									getStepIndex(currentStep()) === i
										? 'bg-[#5d202d] text-white ring-4 ring-[#5d202d]/20'
										: getStepIndex(currentStep()) > i
											? 'bg-green-500 text-white'
											: 'bg-gray-200 text-gray-500'
								"
							>
								@if (getStepIndex(currentStep()) > i) {
									<svg
										class="w-5 h-5"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M5 13l4 4L19 7"
										/>
									</svg>
								} @else {
									<span class="text-sm font-semibold">{{ i + 1 }}</span>
								}
							</div>

							<!-- Step Label -->
							<div class="flex-1 pt-1.5 pb-6">
								<p
									class="font-medium transition-colors"
									[class]="
										getStepIndex(currentStep()) === i
											? 'text-[#5d202d]'
											: getStepIndex(currentStep()) > i
												? 'text-green-600'
												: 'text-gray-400'
									"
								>
									{{ step.label }}
								</p>
								<p class="text-xs text-gray-400 mt-0.5">{{ step.description }}</p>
							</div>
						</div>
					}
				</div>
			</div>

			<!-- Booking Summary (shows collected data) -->
			@if (getStepIndex(currentStep()) >= 1 && currentStep() !== 'success') {
				<div class="p-6 border-t border-[#5d202d]/10 bg-white/50">
					<h4 class="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
						<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
							/>
						</svg>
						Your Booking
					</h4>
					<div class="space-y-2 text-sm">
						@if (firstName() || lastName()) {
							<div class="flex items-center gap-2 text-gray-600">
								<svg
									class="w-4 h-4 text-[#5d202d]"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
									/>
								</svg>
								<span>{{ firstName() }} {{ lastName() }}</span>
							</div>
						}
						@if (selectedDate()) {
							<div class="flex items-center gap-2 text-gray-600">
								<svg
									class="w-4 h-4 text-[#5d202d]"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
									/>
								</svg>
								<span>{{ formatShortDate(selectedDate()!) }}</span>
							</div>
						}
						@if (selectedTimeSlot()) {
							<div class="flex items-center gap-2 text-gray-600">
								<svg
									class="w-4 h-4 text-[#5d202d]"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								<span>{{ selectedTimeSlot()?.display }}</span>
							</div>
						}
						@if (selectedService()) {
							<div class="flex items-center gap-2 text-gray-600">
								<svg
									class="w-4 h-4 text-[#5d202d]"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
									/>
								</svg>
								<span class="truncate">{{ selectedService()?.service_name }}</span>
							</div>
							<!-- price removed per requirement -->
						}
					</div>
				</div>
			}
		</aside>

		<!-- ============================================ -->
		<!-- RIGHT CONTENT AREA -->
		<!-- ============================================ -->
		<div class="flex flex-col h-full max-h-[90vh]">
			<!-- Mobile Progress Bar -->
			<div class="lg:hidden px-6 pt-6 pb-4 border-b border-gray-100">
				<div class="flex items-center justify-between mb-3">
					<span class="text-sm font-medium text-gray-500"
						>Step {{ getStepIndex(currentStep()) + 1 }} of
						{{ getVisibleSteps().length }}</span
					>
					<button
						(click)="closeModal()"
						class="p-2 rounded-full hover:bg-gray-100 transition-colors"
					>
						<svg
							class="w-5 h-5 text-gray-500"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>
				<div class="flex gap-1.5">
					@for (step of getVisibleSteps(); track step.id; let i = $index) {
						<div
							class="flex-1 h-2 rounded-full transition-all"
							[class]="
								getStepIndex(currentStep()) === i
									? 'bg-[#5d202d]'
									: getStepIndex(currentStep()) > i
										? 'bg-green-500'
										: 'bg-gray-200'
							"
						></div>
					}
				</div>
			</div>

			<!-- Desktop Close Button -->
			<button
				(click)="closeModal()"
				class="hidden lg:flex absolute top-5 right-5 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
			>
				<svg
					class="w-5 h-5 text-gray-600"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M6 18L18 6M6 6l12 12"
					/>
				</svg>
			</button>

			<!-- Scrollable Content -->
			<div class="flex-1 overflow-y-auto p-6 lg:p-10">
				<!-- ==================== STEP 1: EMAIL ==================== -->
				@if (currentStep() === 'email') {
					<div class="step-content max-w-md mx-auto text-center">
						<!-- Icon -->
						<div
							class="icon-float w-20 h-20 bg-gradient-to-br from-[#5d202d]/10 to-[#5d202d]/5 rounded-2xl flex items-center justify-center mx-auto mb-6"
						>
							<svg
								class="w-10 h-10 text-[#5d202d]"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
								/>
							</svg>
						</div>

						<!-- Heading -->
						<h2 class="text-2xl lg:text-3xl font-bold text-[#5d202d] mb-2">
							Book Your Appointment
						</h2>
						<p class="text-gray-600 mb-8">Enter your email to get started</p>

						<!-- Error Message -->
						@if (errorMessage()) {
							<div
								class="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center gap-3"
							>
								<svg
									class="w-5 h-5 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								{{ errorMessage() }}
							</div>
						}

						<!-- Form -->
						<div class="space-y-5">
							<input
								type="email"
								[(ngModel)]="email"
								placeholder="Enter your email address"
								class="input-field w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#5d202d]/10 focus:border-[#5d202d] transition-all text-lg"
								(keyup.enter)="sendOtp()"
							/>

							<button
								(click)="sendOtp()"
								[disabled]="!isEmailValid() || isSendingOtp()"
								class="btn-primary w-full py-4 bg-[#5d202d] text-white rounded-xl font-semibold text-lg hover:bg-[#4a1925] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
							>
								@if (isSendingOtp()) {
									<svg
										class="animate-spin h-5 w-5"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											class="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											stroke-width="4"
										></circle>
										<path
											class="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
										></path>
									</svg>
									<span>Sending OTP...</span>
								} @else {
									<span>Continue</span>
								}
							</button>
						</div>

						<!-- Trust Badges -->
						<div
							class="mt-8 flex items-center justify-center gap-6 text-xs text-gray-400"
						>
							<div class="flex items-center gap-1">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
									/>
								</svg>
								<span>Secure</span>
							</div>
							<div class="flex items-center gap-1">
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
								<span>Instant Confirmation</span>
							</div>
						</div>
					</div>
				}

				<!-- ==================== STEP 1B: OTP (part of email verification) ==================== -->
				@if (currentStep() === 'otp') {
					<div class="step-content max-w-md mx-auto text-center">
						<!-- Back Button -->
						<button
							(click)="goBack()"
							class="mb-6 p-2 rounded-full hover:bg-gray-100 transition-colors inline-flex items-center gap-2 text-gray-600"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 19l-7-7 7-7"
								/>
							</svg>
							<span class="text-sm">Change Email</span>
						</button>

						<!-- Icon -->
						<div
							class="icon-float w-20 h-20 bg-gradient-to-br from-[#5d202d]/10 to-[#5d202d]/5 rounded-2xl flex items-center justify-center mx-auto mb-6"
						>
							<svg
								class="w-10 h-10 text-[#5d202d]"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
								/>
							</svg>
						</div>

						<!-- Heading -->
						<h2 class="text-2xl lg:text-3xl font-bold text-[#5d202d] mb-2">
							Verify Your Email
						</h2>
						<p class="text-gray-600 mb-1">Enter the 6-digit code sent to</p>
						<p class="text-[#5d202d] font-semibold mb-8">{{ email() }}</p>

						<!-- Error Message -->
						@if (errorMessage()) {
							<div
								class="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center gap-3"
							>
								<svg
									class="w-5 h-5 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								{{ errorMessage() }}
							</div>
						}

						<!-- OTP Inputs -->
						<div class="flex justify-center gap-3 mb-8">
							@for (digit of otpDigits(); track $index; let i = $index) {
								<input
									[id]="'otp-' + i"
									type="text"
									inputmode="numeric"
									maxlength="1"
									[value]="digit"
									(input)="onOtpInput(i, $event)"
									(keydown)="onOtpKeydown(i, $event)"
									(paste)="onOtpPaste($event)"
									class="otp-input w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#5d202d]/10 focus:border-[#5d202d] transition-all"
								/>
							}
						</div>

						<!-- Verify Button -->
						<button
							(click)="verifyOtp()"
							[disabled]="!isOtpComplete() || isVerifyingOtp()"
							class="btn-primary w-full py-4 bg-[#5d202d] text-white rounded-xl font-semibold text-lg hover:bg-[#4a1925] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 mb-6"
						>
							@if (isVerifyingOtp()) {
								<svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
									<circle
										class="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										stroke-width="4"
									></circle>
									<path
										class="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
									></path>
								</svg>
								<span>Verifying...</span>
							} @else {
								<span>Verify & Continue</span>
							}
						</button>

						<!-- Resend -->
						<p class="text-sm text-gray-500">
							@if (canResendOtp()) {
								Didn't receive the code?
								<button
									(click)="resendOtp()"
									class="text-[#5d202d] font-semibold hover:underline"
								>
									Resend OTP
								</button>
							} @else {
								<span class="inline-flex items-center gap-2">
									Resend in {{ resendCountdown() }}s
								</span>
							}
						</p>
					</div>
				}

				<!-- ==================== STEP 2: DETAILS ==================== -->
				@if (currentStep() === 'details') {
					<div class="step-content max-w-lg mx-auto">
						<!-- Back Button -->
						<button
							(click)="goBack()"
							class="mb-6 p-2 rounded-full hover:bg-gray-100 transition-colors inline-flex items-center gap-2 text-gray-600"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 19l-7-7 7-7"
								/>
							</svg>
							<span class="text-sm">Back</span>
						</button>

						<!-- Heading -->
						<div class="text-center mb-8">
							<h2 class="text-2xl lg:text-3xl font-bold text-[#5d202d] mb-2">
								Your Details
							</h2>
							<p class="text-gray-600">Please provide your contact information</p>
						</div>

						<!-- Form -->
						<div class="space-y-5">
							<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-2"
										>First Name *</label
									>
									<input
										type="text"
										[(ngModel)]="firstName"
										placeholder="John"
										class="input-field w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#5d202d]/10 focus:border-[#5d202d] transition-all"
									/>
								</div>
								<div>
									<label class="block text-sm font-medium text-gray-700 mb-2"
										>Last Name *</label
									>
									<input
										type="text"
										[(ngModel)]="lastName"
										placeholder="Doe"
										class="input-field w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#5d202d]/10 focus:border-[#5d202d] transition-all"
									/>
								</div>
							</div>

							<div>
								<label class="block text-sm font-medium text-gray-700 mb-2"
									>Phone Number *</label
								>
								<div class="relative">
									<span
										class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
										>+91</span
									>
									<input
										type="tel"
										[(ngModel)]="phone"
										placeholder="98765 43210"
										class="input-field w-full pl-14 pr-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#5d202d]/10 focus:border-[#5d202d] transition-all"
									/>
								</div>
								<p class="text-xs text-gray-400 mt-1.5">
									We'll send appointment reminders to this number
								</p>
							</div>

							<button
								(click)="submitDetails()"
								[disabled]="!isDetailsValid()"
								class="btn-primary w-full py-4 bg-[#5d202d] text-white rounded-xl font-semibold text-lg hover:bg-[#4a1925] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 mt-8"
							>
								<span>Continue</span>
							</button>
						</div>
					</div>
				}

				<!-- ==================== STEP 3: DATE & TIME (Combined) ==================== -->
				@if (currentStep() === 'datetime') {
					<div class="step-content">
						<!-- Back Button -->
						<button
							(click)="goBack()"
							class="mb-6 p-2 rounded-full hover:bg-gray-100 transition-colors inline-flex items-center gap-2 text-gray-600"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 19l-7-7 7-7"
								/>
							</svg>
							<span class="text-sm">Back</span>
						</button>

						<!-- Heading -->
						<div class="text-center mb-6">
							<h2 class="text-2xl lg:text-3xl font-bold text-[#5d202d] mb-2">
								Select Date & Time
							</h2>
							<p class="text-gray-600">Choose your preferred appointment slot</p>
						</div>

						<div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
							<!-- Calendar Section -->
							<div>
								<!-- Legend -->
								<div class="flex flex-wrap gap-3 mb-4 text-xs">
									<div class="flex items-center gap-1.5">
										<div class="w-3 h-3 rounded-full bg-green-500"></div>
										<span class="text-gray-600">Available</span>
									</div>
									<div class="flex items-center gap-1.5">
										<div class="w-3 h-3 rounded-full bg-orange-500"></div>
										<span class="text-gray-600">Limited</span>
									</div>
									<div class="flex items-center gap-1.5">
										<div class="w-3 h-3 rounded-full bg-gray-300"></div>
										<span class="text-gray-600">Unavailable</span>
									</div>
								</div>

								@if (isLoading() && !selectedDate()) {
									<div class="flex justify-center py-16 bg-gray-50 rounded-xl">
										<svg
											class="animate-spin h-10 w-10 text-[#5d202d]"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle
												class="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												stroke-width="4"
											></circle>
											<path
												class="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
											></path>
										</svg>
									</div>
								} @else {
									<!-- Calendar Grid -->
									<div class="bg-gray-50 rounded-xl p-4">
										<!-- Month Navigation -->
										<div class="flex items-center justify-between mb-4">
											<button
												(click)="prevMonth()"
												class="p-2 hover:bg-gray-200 rounded-lg transition-colors"
											>
												<svg
													class="w-5 h-5"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M15 19l-7-7 7-7"
													/>
												</svg>
											</button>
											<span class="text-lg font-bold text-gray-800">{{
												currentMonthLabel()
											}}</span>
											<button
												(click)="nextMonth()"
												class="p-2 hover:bg-gray-200 rounded-lg transition-colors"
											>
												<svg
													class="w-5 h-5"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M9 5l7 7-7 7"
													/>
												</svg>
											</button>
										</div>

										<!-- Day Headers -->
										<div class="grid grid-cols-7 gap-1 mb-2">
											@for (
												day of ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
												track $index
											) {
												<div
													class="text-center text-xs font-semibold text-gray-500 py-1"
												>
													{{ day }}
												</div>
											}
										</div>

										<!-- Calendar Days -->
										<div class="grid grid-cols-7 gap-1">
											@for (day of calendarAvailability(); track day.date) {
												<button
													(click)="selectDate(day)"
													[disabled]="
														day.status === 'gray' ||
														day.is_past ||
														day.is_weekly_off
													"
													class="calendar-day relative aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all"
													[class.opacity-40]="
														day.is_past || day.is_weekly_off
													"
													[class.cursor-not-allowed]="
														day.status === 'gray' ||
														day.is_past ||
														day.is_weekly_off
													"
													[class.cursor-pointer]="
														day.status !== 'gray' &&
														!day.is_past &&
														!day.is_weekly_off
													"
													[class.ring-2]="selectedDate() === day.date"
													[class.ring-offset-1]="
														selectedDate() === day.date
													"
													[class.ring-[#5d202d]]="
														selectedDate() === day.date
													"
													[class.bg-[#5d202d]]="
														selectedDate() === day.date
													"
													[class.text-white]="selectedDate() === day.date"
													[class.bg-green-100]="
														day.status === 'green' &&
														selectedDate() !== day.date
													"
													[class.bg-orange-100]="
														day.status === 'orange' &&
														selectedDate() !== day.date
													"
													[class.bg-white]="
														day.status === 'gray' &&
														selectedDate() !== day.date
													"
												>
													<span
														class="font-medium"
														[class.font-bold]="day.is_today"
													>
														{{ day.date.split('-')[2] | number }}
													</span>

													@if (
														day.is_today && selectedDate() !== day.date
													) {
														<span
															class="absolute bottom-0.5 w-1 h-1 rounded-full bg-[#5d202d]"
														></span>
													}
												</button>
											}
										</div>
									</div>
								}
							</div>

							<!-- Time Slots Section -->
							<div>
								<h3 class="text-sm font-semibold text-gray-700 mb-3">
									@if (selectedDate()) {
										Available Times for {{ formatShortDate(selectedDate()!) }}
									} @else {
										Select a date to see available times
									}
								</h3>

								@if (!selectedDate()) {
									<div class="bg-gray-50 rounded-xl p-8 text-center">
										<svg
											class="w-12 h-12 text-gray-300 mx-auto mb-3"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
											/>
										</svg>
										<p class="text-gray-500">Please select a date first</p>
									</div>
								} @else if (isLoading()) {
									<div class="flex justify-center py-12 bg-gray-50 rounded-xl">
										<svg
											class="animate-spin h-8 w-8 text-[#5d202d]"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle
												class="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												stroke-width="4"
											></circle>
											<path
												class="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
											></path>
										</svg>
									</div>
								} @else if (timeSlots().length === 0) {
									<div class="bg-gray-50 rounded-xl p-8 text-center">
										<svg
											class="w-12 h-12 text-gray-300 mx-auto mb-3"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
										<p class="text-gray-500">
											No available slots for this date
										</p>
									</div>
								} @else {
									<div class="max-h-[350px] overflow-y-auto pr-2">
										<!-- All Time Slots in Single Grid -->
										<div class="grid grid-cols-3 sm:grid-cols-4 gap-2">
											@for (slot of timeSlots(); track slot.start) {
												<button
													(click)="selectTimeSlot(slot)"
													class="time-slot py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all"
													[class.border-[#5d202d]]="
														selectedTimeSlot()?.start === slot.start
													"
													[class.bg-[#5d202d]]="
														selectedTimeSlot()?.start === slot.start
													"
													[class.text-white]="
														selectedTimeSlot()?.start === slot.start
													"
													[class.border-gray-200]="
														selectedTimeSlot()?.start !== slot.start
													"
													[class.hover:border-[#5d202d]/50]="
														selectedTimeSlot()?.start !== slot.start
													"
												>
													{{ slot.display }}
												</button>
											}
										</div>
									</div>
								}
							</div>
						</div>

						<!-- Continue Button -->
						<div class="mt-8">
							<button
								(click)="proceedToConfirm()"
								[disabled]="!isDateTimeValid()"
								class="btn-primary w-full py-4 bg-[#5d202d] text-white rounded-xl font-semibold text-lg hover:bg-[#4a1925] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
							>
								<span>Continue</span>
							</button>
						</div>
					</div>
				}

				<!-- ==================== STEP 4: SERVICE & CONFIRM (Combined) ==================== -->
				@if (currentStep() === 'confirm') {
					<div class="step-content max-w-3xl mx-auto">
						<!-- Back Button (show when there's an error) -->
						@if (errorMessage()) {
							<button
								(click)="goBack()"
								class="mb-4 p-2 rounded-full hover:bg-gray-100 transition-colors inline-flex items-center gap-2 text-gray-600"
							>
								<svg
									class="w-5 h-5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M15 19l-7-7 7-7"
									/>
								</svg>
								<span class="text-sm">Back</span>
							</button>
						}

						<!-- First Consultation Free Banner -->
						@if (!existingCustomer()?.has_had_consultation) {
							<div
								class="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl flex items-center gap-3"
							>
								<div
									class="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0"
								>
									<svg
										class="w-5 h-5 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
										/>
									</svg>
								</div>
								<div>
									<p class="font-semibold text-green-800">
										🎉 First Consultation is FREE!
									</p>
									<p class="text-sm text-green-700">
										Welcome! Your first consultation with us is complimentary.
									</p>
								</div>
							</div>
						}

						<!-- Heading -->
						<div class="text-center mb-6">
							<h2 class="text-2xl lg:text-3xl font-bold text-[#5d202d] mb-2">
								Choose Consultation & Confirm
							</h2>
							<p class="text-gray-600">
								Select your consultation type and confirm your booking
							</p>
						</div>

						<!-- Error Message -->
						@if (errorMessage()) {
							<div
								class="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center gap-3"
							>
								<svg
									class="w-5 h-5 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								{{ errorMessage() }}
							</div>
						}

						<div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
							<!-- Left side - Consultation Mode & Notes (3 cols) -->
							<div class="lg:col-span-3">
								<!-- Consultation Mode Toggle -->
								<div class="mt-0">
									<h3 class="text-sm font-semibold text-gray-700 mb-3">
										Consultation Mode
									</h3>
									<div class="flex gap-3">
										<button
											(click)="consultationMode.set('offline')"
											class="flex-1 py-3 px-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all"
											[class.border-[#5d202d]]="
												consultationMode() === 'offline'
											"
											[class.bg-[#5d202d]/5]="
												consultationMode() === 'offline'
											"
											[class.text-[#5d202d]]="
												consultationMode() === 'offline'
											"
											[class.border-gray-200]="
												consultationMode() !== 'offline'
											"
											[class.hover:border-gray-300]="
												consultationMode() !== 'offline'
											"
										>
											<svg
												class="w-5 h-5"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
												/>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
												/>
											</svg>
											<span class="font-medium">In-Person</span>
										</button>
										<button
											(click)="consultationMode.set('online')"
											class="flex-1 py-3 px-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all"
											[class.border-blue-500]="
												consultationMode() === 'online'
											"
											[class.bg-blue-50]="consultationMode() === 'online'"
											[class.text-blue-600]="consultationMode() === 'online'"
											[class.border-gray-200]="
												consultationMode() !== 'online'
											"
											[class.hover:border-gray-300]="
												consultationMode() !== 'online'
											"
										>
											<svg
												class="w-5 h-5"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
												/>
											</svg>
											<span class="font-medium">Online (Zoom)</span>
										</button>
									</div>
									@if (consultationMode() === 'online') {
										<p
											class="mt-2 text-xs text-blue-600 bg-blue-50 p-2 rounded-lg"
										>
											📹 A Zoom meeting link will be sent to your email after
											booking.
										</p>
									}
								</div>

								<!-- Notes -->
								<div class="mt-6">
									<label class="block text-sm font-medium text-gray-700 mb-2"
										>Notes</label
									>
									<textarea
										[(ngModel)]="notes"
										rows="3"
										placeholder="What Service are you looking for?"
										maxlength="500"
										class="input-field w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#5d202d]/10 focus:border-[#5d202d] transition-all resize-none text-sm"
									></textarea>
									<p class="text-xs text-gray-400 mt-1.5">
										{{ notes().length }}/500 characters
									</p>
								</div>
							</div>

							<!-- Right side - Booking Summary (2 cols) -->
							<div class="lg:col-span-2">
								<h3 class="text-sm font-semibold text-gray-700 mb-3">
									Booking Summary
								</h3>

								<!-- Summary Card -->
								<div class="bg-gray-50 rounded-xl p-5 space-y-4">
									<div
										class="flex items-center gap-3 pb-3 border-b border-gray-200"
									>
										<div
											class="w-10 h-10 rounded-lg bg-[#5d202d]/10 flex items-center justify-center flex-shrink-0"
										>
											<svg
												class="w-5 h-5 text-[#5d202d]"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
												/>
											</svg>
										</div>
										<div class="flex-1 min-w-0">
											<p class="text-xs text-gray-500">Customer</p>
											<p class="font-medium text-gray-800 truncate">
												{{ firstName() }} {{ lastName() }}
											</p>
										</div>
									</div>

									<div
										class="flex items-center gap-3 pb-3 border-b border-gray-200"
									>
										<div
											class="w-10 h-10 rounded-lg bg-[#5d202d]/10 flex items-center justify-center flex-shrink-0"
										>
											<svg
												class="w-5 h-5 text-[#5d202d]"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
												/>
											</svg>
										</div>
										<div>
											<p class="text-xs text-gray-500">Date & Time</p>
											<p class="font-medium text-gray-800">
												{{ formatShortDate(selectedDate()!) }}
											</p>
											<p class="text-sm text-gray-600">
												{{ selectedTimeSlot()?.display }}
											</p>
										</div>
									</div>

									<div
										class="flex items-center gap-3 pb-3 border-b border-gray-200"
									>
										<div
											class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
											[class.bg-blue-50]="consultationMode() === 'online'"
											[class.bg-[#5d202d]/10]="
												consultationMode() === 'offline'
											"
										>
											@if (consultationMode() === 'online') {
												<svg
													class="w-5 h-5 text-blue-600"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
													/>
												</svg>
											} @else {
												<svg
													class="w-5 h-5 text-[#5d202d]"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
													/>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
													/>
												</svg>
											}
										</div>
										<div class="flex-1 min-w-0">
											<p class="text-xs text-gray-500">Mode</p>
											<p class="font-medium text-gray-800">
												{{
													consultationMode() === 'online'
														? 'Online (Zoom)'
														: 'In-Person'
												}}
											</p>
										</div>
									</div>
								</div>

								<!-- Confirm Button -->
								<button
									(click)="confirmBooking()"
									[disabled]="!isConfirmValid() || isBooking()"
									class="btn-primary w-full py-4 bg-[#5d202d] text-white rounded-xl font-semibold text-lg hover:bg-[#4a1925] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 mt-4"
								>
									@if (isBooking()) {
										<svg
											class="animate-spin h-5 w-5"
											fill="none"
											viewBox="0 0 24 24"
										>
											<circle
												class="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												stroke-width="4"
											></circle>
											<path
												class="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
											></path>
										</svg>
										<span>Booking...</span>
									} @else {
										<svg
											class="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M5 13l4 4L19 7"
											/>
										</svg>
										<span>Confirm Booking</span>
									}
								</button>
							</div>
						</div>
					</div>
				}

				<!-- ==================== SUCCESS ==================== -->
				@if (currentStep() === 'success') {
					<div class="step-content max-w-lg mx-auto text-center py-8">
						<!-- Success Animation -->
						<div class="success-checkmark mb-8">
							<div
								class="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto relative"
							>
								<svg
									class="w-12 h-12 text-green-500 checkmark-icon"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="3"
										d="M5 13l4 4L19 7"
									/>
								</svg>
								<!-- Confetti particles -->
								<div class="confetti-container">
									<div class="confetti confetti-1"></div>
									<div class="confetti confetti-2"></div>
									<div class="confetti confetti-3"></div>
									<div class="confetti confetti-4"></div>
									<div class="confetti confetti-5"></div>
									<div class="confetti confetti-6"></div>
								</div>
							</div>
						</div>

						<!-- Heading -->
						<h2 class="text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
							Booking Confirmed!
						</h2>
						<p class="text-gray-600 mb-6">
							Your appointment has been successfully booked
						</p>

						<!-- Booking Reference -->
						<div
							class="bg-gradient-to-br from-[#5d202d]/10 to-[#5d202d]/5 rounded-2xl p-5 mb-6 inline-block"
						>
							<p class="text-sm text-gray-500 mb-1">Booking Reference</p>
							<p class="text-2xl font-bold text-[#5d202d] tracking-wide">
								#{{ bookingReference() }}
							</p>
						</div>

						<!-- Confirmation Details -->
						<div class="bg-gray-50 rounded-xl p-4 mb-6 text-left text-sm">
							<div class="space-y-2">
								<div class="flex justify-between">
									<span class="text-gray-500">Date</span>
									<span class="font-medium">{{
										formatShortDate(selectedDate()!)
									}}</span>
								</div>
								<div class="flex justify-between">
									<span class="text-gray-500">Time</span>
									<span class="font-medium">{{
										selectedTimeSlot()?.display
									}}</span>
								</div>
								<div class="flex justify-between">
									<span class="text-gray-500">Service</span>
									<span class="font-medium">{{
										selectedService()?.service_name
									}}</span>
								</div>
								<div class="flex justify-between">
									<span class="text-gray-500">Mode</span>
									<span class="font-medium">
										{{
											consultationMode() === 'online'
												? 'Online (Zoom)'
												: 'In-Person'
										}}
									</span>
								</div>
							</div>
						</div>

						<!-- Email Notice -->
						<p class="text-sm text-gray-500 mb-6">
							Confirmation
							@if (consultationMode() === 'online') {
								& Zoom link
							}
							sent to <strong class="text-[#5d202d]">{{ email() }}</strong>
						</p>

						<!-- Action Buttons -->
						<div class="flex flex-col sm:flex-row gap-3">
							<button
								(click)="bookAnother()"
								class="flex-1 py-3 px-6 border-2 border-[#5d202d] text-[#5d202d] rounded-xl font-semibold hover:bg-[#5d202d]/5 transition-all"
							>
								Book Another
							</button>
							<button
								(click)="closeModal()"
								class="flex-1 py-3 px-6 bg-[#5d202d] text-white rounded-xl font-semibold hover:bg-[#4a1925] transition-all"
							>
								Done
							</button>
						</div>
					</div>
				}
			</div>
		</div>
	</div>
</div>
